import { useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { createOrganization } from '@/api/organization.api';
import { useIndicatorStore } from '@/stores/indicator.store';
import { useToastStore } from '@/stores/toast.store';
import BaseModal from '@/components/modal/BaseModal';

interface CreateOrganizationModalProps {
  showModal: boolean;
  handleShowModal: () => void;
  storeName: string;
  storeId: number;
  teamAuthId: string;
  onOrganizationCreated?: (orgData: { organizationId: string; organizationName: string }) => void;
}

const CreateOrganizationModal = ({ showModal, handleShowModal, storeId, teamAuthId, onOrganizationCreated }: CreateOrganizationModalProps) => {
  const { setShowToast, setToastInfos } = useToastStore();
  const setShowIndicator = useIndicatorStore((state) => state.setShowIndicator);
  const [organizationName, setOrganizationName] = useState('');

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      setToastInfos({ text: '조직 이름을 입력해주세요.' });
      setShowToast(true);
      return;
    }

    setShowIndicator(true);

    try {
      const result = await createOrganization(teamAuthId, storeId, organizationName);

      if (!result.isSuccess || !result.data) {
        setToastInfos({ text: result.message || '조직 생성에 실패했습니다.' });
        setShowToast(true);
        setShowIndicator(false);
        return;
      }

      // 조직 생성 성공
      const newOrgId = result.data.organizationId;

      setToastInfos({ text: '조직이 성공적으로 생성되었습니다.' });
      setShowToast(true);

      // 성공 콜백 호출
      if (onOrganizationCreated) {
        onOrganizationCreated({
          organizationId: newOrgId,
          organizationName: organizationName
        });
      }
    } catch (error) {
      console.error('조직 생성 중 오류 발생:', error);
      setToastInfos({ text: '조직 생성 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setShowIndicator(false);
    }
  };

  return (
    <BaseModal
      showModal={showModal}
      handleShowModal={handleShowModal}
      modalTitle="조직 생성"
      buttons={{
        primary: {
          title: '생성하기',
          onClick: handleCreateOrganization
        },
        secondary: {
          title: '취소',
          onClick: handleShowModal
        }
      }}
      style={{ maxWidth: '446px' }}
    >
      <Stack gap="16px" width="100%" padding="0 20px 20px">
        <Typography variant="body1">병원을 관리할 새 조직을 생성합니다.</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          조직 이름
        </Typography>
        <TextField placeholder="조직 이름을 입력해주세요" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} fullWidth />
      </Stack>
    </BaseModal>
  );
};

export default CreateOrganizationModal;
